import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCms } from '../cms/CmsContext';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import SponsorsSection from '../components/SponsorsSection';
import ScrollToTop from '../components/ScrollToTop';
import { getArabicTranslation } from '../utils/arabicTranslations';
import { getCmsImagePath } from '../cms/cmsData';
import { getArtistLineup, getDay, getFestivalSummary, getLineupArtists, getLineupSummary, getStage } from '../data/artistLineup';
import './HomePage.css';

const HomeLineupSummary = ({ rows, note }) => (
  <div className="experience-jump-lineup-summary" role="list" aria-label="Artist lineup by day">
    {rows.map((row) => (
      <div key={row.day} className="experience-jump-lineup-row" role="listitem">
        <span className="experience-jump-lineup-day">{row.day}:</span>{' '}
        <span className="experience-jump-lineup-artists">{row.artists}</span>
      </div>
    ))}
    {note ? <p className="experience-jump-lineup-note">{note}</p> : null}
  </div>
);

const TICKET_OPTIONS = [
  {
    id: 'festival',
    title: 'Festival Pass',
    arabicTitle: 'تذاكر المهرجان',
    description: 'Access to festival grounds, stages, food vendors, bazaar, rides, and all cultural experiences.',
    options: [
      { name: '1-Day Pass', price: '$20', link: '/festival-tickets', image: getCmsImagePath('product1DayTickets', '/product-1-day-tickets.webp') },
      { name: '2-Day Pass', price: '$30', link: '/festival-tickets', image: getCmsImagePath('product2DayTickets', '/product-2-day-tickets.webp') },
      { name: '3-Day Pass', price: '$40', link: '/festival-tickets', image: getCmsImagePath('product3DayTickets', '/product-3-day-tickets.webp') },
    ],
    icon: '\u{1F3AA}',
    featured: true
  },
  {
    id: 'amphitheater',
    title: 'Pacific Amphitheatre',
    arabicTitle: 'المسرح الرئيسي لحفلة الغناء',
    description: 'Reserved seating for world-class headliners. Includes festival admission for the same day.',
    options: [
      { name: 'Saturday', price: 'Starting at $32', link: '/amphitheater-tickets-saturday', image: '/Artist Collage Funnel Page -- Saturday - Full (1).webp' },
      { name: 'Sunday', price: 'Starting at $32', link: '/amphitheater-tickets-sunday', image: '/Artist Collage Funnel Page -- Sunday.webp' },
    ],
    icon: '\u{1F3AD}',
    featured: true
  },
  {
    id: 'vendor',
    title: 'Become a Vendor',
    arabicTitle: 'كن بائعاً',
    description: 'Join our vibrant marketplace. Showcase your products or cuisine to thousands of attendees.',
    types: [
      { name: 'Bazaar Vendors', arabicName: 'البازارات', description: 'Arab & North African boutiques, fashion, hijab, accessories, jewelry, art items, perfumes, books, and more', link: '/bazaar-vendor' },
      { name: 'Food & Beverage Vendors', arabicName: 'الطعام والمشروبات', description: 'Arab, North African, and American cuisine, desserts, and beverages from food trucks and booths', link: '/food-vendor' },
    ],
    icon: '🏪',
    featured: false
  },
  {
    id: 'info',
    title: 'More Information',
    arabicTitle: '',
    description: 'Please browse our site for more information. You can purchase tickets, booths, and inquire about partnerships or sponsorship opportunities!',
    infoCard: true,
    images: [
      '/ocmena-official-flyer(1080x1350)-c.png',
      '/how_tickets_work.jpeg',
      '/map.png'
    ],
    icon: 'ℹ️',
    featured: false
  },
];

const HomePage = ({ onGetTicketsClick }) => {
  const { content } = useCms();
  const cms = content?.homePage || {};

  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const [activeCard, setActiveCard] = useState(0);
  const [activeAmpCard, setActiveAmpCard] = useState(0);
  const [heroVideoEnabled, setHeroVideoEnabled] = useState(false);
  const swipeStartXRef = useRef({ festival: null, amp: null });
  const { artistCards, ampArtistCards, homepageFestivalLineup, homepageAmpLineup } = useMemo(() => {
    const artistLineup = getArtistLineup(content);
    const toCarouselCard = (artist) => ({
      src: artist.carouselImage || artist.image || artist.musicImage,
      alt: artist.name,
      arabic: artist.arabicName || '',
      meta: artist.stage === 'pac-amp'
        ? `${(artist.days || []).map((day) => getDay(day)?.label).filter(Boolean).join(' / ')} · Pacific Amphitheatre`
        : `OC FAIRGROUNDS<br/>(${getStage(artist.stage)?.label || artist.stage})`,
    });
    return {
      artistCards: getLineupArtists(artistLineup, { role: 'artist', homepageOnly: true })
        .filter((artist) => artist.stage !== 'pac-amp' && (artist.carouselImage || artist.image || artist.musicImage))
        .map(toCarouselCard),
      ampArtistCards: getLineupArtists(artistLineup, { stage: 'pac-amp', role: 'artist', homepageOnly: true })
        .filter((artist) => artist.carouselImage || artist.image || artist.musicImage)
        .map(toCarouselCard),
      homepageFestivalLineup: getFestivalSummary(artistLineup),
      homepageAmpLineup: getLineupSummary(artistLineup, 'pac-amp'),
    };
  }, [content]);

  const wrapIndex = (index, cards) => cards.length ? ((index % cards.length) + cards.length) % cards.length : 0;

  const getVisibleSides = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1200) return 4;
      if (window.innerWidth >= 900) return 3;
      return 2;
    }
    return 2;
  };

  const getCardSpacing = () => {
    if (typeof window !== 'undefined') {
      const sides = getVisibleSides();
      const halfVW = window.innerWidth / 2;
      const cardHalfW = (240 * 1.06) / 2;
      return Math.max(25, (halfVW - cardHalfW * 1.05) / sides);
    }
    return 50;
  };

  const signedOffset = (index, active, cards) => {
    let off = index - active;
    const n = cards.length;
    if (off > n / 2) off -= n;
    if (off < -n / 2) off += n;
    return off;
  };

  const goTo = (index) => {
    setActiveCard(wrapIndex(index, artistCards));
  };

  const goToAmp = (index) => {
    setActiveAmpCard(wrapIndex(index, ampArtistCards));
  };

  const handleSwipeStart = (carousel, clientX) => {
    swipeStartXRef.current[carousel] = clientX;
  };

  const handleSwipeEnd = (carousel, clientX) => {
    const startX = swipeStartXRef.current[carousel];
    swipeStartXRef.current[carousel] = null;
    if (startX === null) return;

    const swipeDistance = clientX - startX;
    const swipeThreshold = 35;
    if (Math.abs(swipeDistance) < swipeThreshold) return;

    if (carousel === 'festival') {
      goTo(activeCard + (swipeDistance < 0 ? 1 : -1));
      return;
    }

    goToAmp(activeAmpCard + (swipeDistance < 0 ? 1 : -1));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCard(prev => wrapIndex(prev + 1, artistCards));
    }, 2500);
    return () => clearInterval(interval);
  }, [artistCards]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveAmpCard(prev => wrapIndex(prev + 1, ampArtistCards));
    }, 2500);
    return () => clearInterval(interval);
  }, [ampArtistCards]);

  useEffect(() => {
    const enableVideo = () => setHeroVideoEnabled(true);
    if (document.readyState === 'complete') {
      enableVideo();
      return undefined;
    }
    window.addEventListener('load', enableVideo, { once: true });
    return () => window.removeEventListener('load', enableVideo);
  }, []);

  useEffect(() => {
    const targetDate = new Date('June 19, 2026 11:00:00 GMT-0700').getTime();
    if (isNaN(targetDate)) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="page-wrapper">
      <AnnouncementBar />
      
      {/* Hero Section with Video Background */}
      <section className="home-hero-section">
        <div className="hero-background-wrapper">
          <video
            src={heroVideoEnabled ? '/hero-final.mp4' : undefined}
            aria-label="OC MENA Festival"
            className="hero-background-video"
            autoPlay
            muted
            loop
            playsInline
            preload="none"
          />
          <div className="hero-gradient-overlay"></div>
        </div>

        <div className="hero-content">
          <h1 className="hero-title">
            {cms.heroTitle}
            <span className="arabic-text on-dark">{getArabicTranslation(cms.heroTitle || 'OC MENA Festival')}</span>
          </h1>
          <p className="hero-subtitle">{cms.heroSubtitle}</p>
          <p className="hero-description">
            {cms.heroDescription}
          </p>

          {/* Countdown */}
          <div className="countdown-container">
            <div className="countdown-item">
              <span className="countdown-number">{timeLeft.days}</span>
              <span className="countdown-label">days</span>
            </div>
            <div className="countdown-item">
              <span className="countdown-number">{timeLeft.hours}</span>
              <span className="countdown-label">hrs</span>
            </div>
            <div className="countdown-item">
              <span className="countdown-number">{timeLeft.minutes}</span>
              <span className="countdown-label">mins</span>
            </div>
            <div className="countdown-item">
              <span className="countdown-number">{timeLeft.seconds}</span>
              <span className="countdown-label">secs</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="hero-buttons">
            <Link to="/vendors" className="btn-outline-white" title="Become a Vendor">{cms.ctaVendorButton}</Link>
            <Link to="/tickets" className="btn-primary-red" title="Get Tickets">{cms.ctaTicketsButton}</Link>
          </div>
        </div>

        {/* Ticket Options Section - Inside Hero for Shared Video Background */}
        <div className="ticket-options-wrapper">
          <div className="options-container">
            <div className="option-category featured">
              <div className="experience-jump-section">
                <h2 className="section-title">
                  Choose Your Experience
                  <span className="arabic-text on-dark">اختر تجربتك</span>
                </h2>
                <p className="section-subtitle" style={{ fontSize: '16px' }}>Select your adventure: Amphitheater Passes or Festival Passes. Both have access to food and bazaar vendors, carnival rides, Watch Party, Hookah Bar, and more.</p>
                <div className="experience-jump-cards">
                  <a
                    href="#homepage-festival-option"
                    className="experience-jump-card experience-jump-card-white"
                    title="Jump to Festival Pass option"
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById('homepage-festival-option');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    <span className="experience-jump-badge">Option 1</span>
                    <strong>Festival Tickets {'\u{1F389}'}</strong>
                    <span className="arabic-text on-dark">تذاكر المهرجان</span>
                    <HomeLineupSummary rows={homepageFestivalLineup} />
                    <div className="experience-jump-card-panel">
                      <span className="experience-jump-learn-more">Learn more</span>
                      <span className="experience-jump-price">Starting at $20</span>
                    </div>
                  </a>
                  <a
                    href="#homepage-pac-amp-option"
                    className="experience-jump-card experience-jump-card-white"
                    title="Jump to Pacific Amphitheatre option"
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById('homepage-pac-amp-option');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    <span className="experience-jump-badge">Option 2</span>
                    <strong>Pacific Amphitheater Tickets {'\u{1F3A4}'}</strong>
                    <span className="arabic-text on-dark">المسرح الرئيسي لحفلة الغناء</span>
                    <HomeLineupSummary
                      rows={homepageAmpLineup}
                      note={<span className="experience-jump-lineup-note-emphasis">*Includes access to festival and indoor stage performances in Option 1</span>}
                    />
                    <div className="experience-jump-card-panel">
                      <span className="experience-jump-learn-more">Learn more</span>
                      <span className="experience-jump-price">Starting at $32</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
            {TICKET_OPTIONS.map((category) => (
              <div
                key={category.id}
                id={category.id === 'festival' ? 'homepage-festival-option' : category.id === 'amphitheater' ? 'homepage-pac-amp-option' : undefined}
                className={`option-category ${category.featured ? 'featured' : ''}`}
              >
                <div className="category-header">
                  <div className="category-heading-row">
                    {category.id === 'festival' && <span className="option-inline-badge">Option 1</span>}
                    {category.id === 'amphitheater' && <span className="option-inline-badge">Option 2</span>}
                    <h3 className="category-title">
                      {category.title}
                      <span className="arabic-text on-dark">{category.arabicTitle}</span>
                    </h3>
                  </div>
                </div>
                <p className="category-description">{category.description}</p>
                {category.infoCard ? (
                  <div className="info-card-content">
                    <div className="info-card-links">
                      <Link to="/tickets" className="info-card-link">Tickets</Link>
                      <span className="info-card-separator">•</span>
                      <Link to="/vendors" className="info-card-link">Booths</Link>
                      <span className="info-card-separator">•</span>
                      <Link to="/partners" className="info-card-link">Partners</Link>
                      <span className="info-card-separator">•</span>
                      <Link to="/sponsors" className="info-card-link">Sponsors</Link>
                    </div>
                    <div className="info-card-images">
                      <img loading="lazy" alt="OC MENA Festival Poster" className="poster-image" src={category.images[0]} />
                      <img loading="lazy" alt="Orange County" className="oc-sign-image" src={category.images[1]} />
                      <img loading="lazy" alt="OC MENA Festival Event Map" className="oc-sign-image info-map-image" src={category.images[2]} />
                    </div>
                  </div>
                ) : category.options ? (
                  <div className="category-options">
                    {category.options.map((option, index) => (
                      <Link key={index} to={option.link} className={`option-card ${category.id === 'amphitheater' ? 'amp-option-card' : ''}`}>
                        <div className="option-details">
                          {option.image && <img loading="lazy" decoding="async" src={option.image} alt={option.name} className={`option-image ${category.id === 'amphitheater' ? 'amphitheater' : ''}`} />}
                        </div>
                        <div className="option-price-group">
                          {category.id === 'amphitheater' ? (
                            <>
                              <span className="option-price">Get {option.name} Tickets!</span>
                              <span className="option-price-subtext">{option.price}</span>
                            </>
                          ) : (
                            <span className="option-price">{option.name} ({option.price})</span>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="category-types">
                    {category.types.map((type, index) => (
                      <Link key={index} to={type.link} className="type-card">
                        <h4 className="type-name">{type.name}</h4>
                        {type.arabicName && <p className="type-arabic-name" style={{ fontSize: '26px', marginTop: '-8px', lineHeight: '1' }}>{type.arabicName}</p>}
                        <p className="type-description">{type.description}</p>
                      </Link>
                    ))}
                  </div>
                )}
                {category.id === 'festival' && (
                  <>
                    <div className="lineup-cta-section">
                      <p className="lineup-text">See who's performing at the festival</p>
                      <p className="lineup-cta-text">Click <Link to="/music-entertainment" className="lineup-cta-link">here</Link> to view our entire Music & entertainment</p>
                    </div>
                    <div className="artist-lineup-grid">
                      <div
                        className="fan-stage"
                        onTouchStart={(e) => handleSwipeStart('festival', e.changedTouches[0].clientX)}
                        onTouchEnd={(e) => handleSwipeEnd('festival', e.changedTouches[0].clientX)}
                        onMouseDown={(e) => handleSwipeStart('festival', e.clientX)}
                        onMouseUp={(e) => handleSwipeEnd('festival', e.clientX)}
                      >
                        <span className="fan-hint left" onClick={() => goTo(activeCard - 1)}>‹</span>
                        <span className="fan-hint right" onClick={() => goTo(activeCard + 1)}>›</span>

                        {artistCards.map((card, index) => {
                          const off = signedOffset(index, activeCard, artistCards);
                          const abs = Math.abs(off);
                          const isActive = (off === 0);
                          const visible = abs <= getVisibleSides();
                          const SPREAD_DEG = 8;
                          const DEPTH_PX = 20;
                          const ACTIVE_LIFT = 22;
                          const ACTIVE_SCALE = 1.06;
                          const INACTIVE_SCALE = 0.88;
                          const CARD_SPACING = getCardSpacing();
                          const VISIBLE_SIDES = getVisibleSides();

                          const rotZ = off * SPREAD_DEG;
                          const transX = off * CARD_SPACING;
                          const transY = isActive ? -ACTIVE_LIFT : (abs * 4);
                          const transZ = -abs * DEPTH_PX;
                          const scale = isActive ? ACTIVE_SCALE : Math.max(INACTIVE_SCALE, 1 - abs * 0.04);
                          const zIndex = isActive ? 100 : (VISIBLE_SIDES - abs + 1);

                          return (
                            <motion.div
                              key={index}
                              className={`fan-card ${isActive ? 'is-active' : ''}`}
                              initial={false}
                              animate={{
                                opacity: visible ? 1 : 0,
                                x: transX,
                                y: transY,
                                z: transZ,
                                rotateZ: rotZ,
                                scale: scale,
                                filter: isActive ? 'brightness(1) saturate(1.05)' : 'brightness(0.5) saturate(0.7)',
                              }}
                              transition={{
                                type: 'spring',
                                stiffness: 100,
                                damping: 25,
                                mass: 1.2,
                              }}
                              style={{
                                pointerEvents: isActive ? 'auto' : 'none',
                                zIndex: zIndex,
                              }}
                              onClick={() => goTo(index)}
                            >
                              <div className="fan-card-img-wrapper">
                                <img loading="lazy" decoding="async" src={card.src} alt={card.alt} className="fan-card-img" />
                              </div>
                              <div className="fan-card-label">
                                <p className="fan-card-arabic">{card.arabic}</p>
                                <p className="fan-card-name">{card.alt}</p>
                                <p className="fan-card-meta" dangerouslySetInnerHTML={{ __html: card.meta }} />
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                      <div className="fan-dots">
                        {artistCards.map((_, index) => (
                          <button
                            key={index}
                            className={`fan-dot ${index === activeCard ? 'is-active' : ''}`}
                            onClick={() => goTo(index)}
                          />
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {category.id === 'amphitheater' && (
                  <>
                    <div className="lineup-cta-section">
                      <p className="lineup-text">See who's performing at Pacific Amphitheatre</p>
                    </div>
                    <div className="artist-lineup-grid">
                      <div
                        className="fan-stage"
                        onTouchStart={(e) => handleSwipeStart('amp', e.changedTouches[0].clientX)}
                        onTouchEnd={(e) => handleSwipeEnd('amp', e.changedTouches[0].clientX)}
                        onMouseDown={(e) => handleSwipeStart('amp', e.clientX)}
                        onMouseUp={(e) => handleSwipeEnd('amp', e.clientX)}
                      >
                        <span className="fan-hint left" onClick={() => goToAmp(activeAmpCard - 1)}>‹</span>
                        <span className="fan-hint right" onClick={() => goToAmp(activeAmpCard + 1)}>›</span>

                        {ampArtistCards.map((card, index) => {
                          const off = signedOffset(index, activeAmpCard, ampArtistCards);
                          const abs = Math.abs(off);
                          const isActive = (off === 0);
                          const visible = abs <= getVisibleSides();
                          const SPREAD_DEG = 8;
                          const DEPTH_PX = 20;
                          const ACTIVE_LIFT = 22;
                          const ACTIVE_SCALE = 1.06;
                          const INACTIVE_SCALE = 0.88;
                          const CARD_SPACING = getCardSpacing();
                          const VISIBLE_SIDES = getVisibleSides();

                          const rotZ = off * SPREAD_DEG;
                          const transX = off * CARD_SPACING;
                          const transY = isActive ? -ACTIVE_LIFT : (abs * 4);
                          const transZ = -abs * DEPTH_PX;
                          const scale = isActive ? ACTIVE_SCALE : Math.max(INACTIVE_SCALE, 1 - abs * 0.04);
                          const zIndex = isActive ? 100 : (VISIBLE_SIDES - abs + 1);

                          return (
                            <motion.div
                              key={index}
                              className={`fan-card ${isActive ? 'is-active' : ''}`}
                              initial={false}
                              animate={{
                                opacity: visible ? 1 : 0,
                                x: transX,
                                y: transY,
                                z: transZ,
                                rotateZ: rotZ,
                                scale: scale,
                                filter: isActive ? 'brightness(1) saturate(1.05)' : 'brightness(0.5) saturate(0.7)',
                              }}
                              transition={{
                                type: 'spring',
                                stiffness: 100,
                                damping: 25,
                                mass: 1.2,
                              }}
                              style={{
                                pointerEvents: isActive ? 'auto' : 'none',
                                zIndex: zIndex,
                              }}
                              onClick={() => goToAmp(index)}
                            >
                              <div className="fan-card-img-wrapper">
                                <img loading="lazy" decoding="async" src={card.src} alt={card.alt} className="fan-card-img" />
                              </div>
                              <div className="fan-card-label">
                                <p className="fan-card-arabic" style={{ paddingTop: card.alt === 'Dystinct' ? '0.5rem' : '0' }}>{card.arabic}</p>
                                <p className="fan-card-name">{card.alt}</p>
                                <p className="fan-card-meta">{card.meta}</p>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                      <div className="fan-dots">
                        {ampArtistCards.map((_, index) => (
                          <button
                            key={index}
                            className={`fan-dot ${index === activeAmpCard ? 'is-active' : ''}`}
                            onClick={() => goToAmp(index)}
                          />
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      <SponsorsSection />

      {/* Footer */}
      <Footer />
      
      <ScrollToTop />
    </div>
  );
};

export default HomePage;
